import type { DwdCheckStatus } from './dwd-check-status.type';
import { TERMINAL_OUTCOMES } from './jobs-api/interfaces/workflow-execution-status.interface';
import type { LicenseQaScenario } from './license-qa.model';
import type { QaForceableEndpoint } from './qa-endpoint-fail.model';
import type { QaInviteRejectMode } from './qa-invite-reject.model';
import type { ForceableBlockVerdict } from './qa-verdict-override.model';

/**
 * The four terminal outcomes a job can report, as the QA lever offers them
 * (EDUS-2770).
 *
 * This IS {@link TERMINAL_OUTCOMES}, not a mirror of it: the QA dropdown offers
 * exactly the buckets a job can report, so a bucket added or renamed there
 * shows up here with no second edit. The QA name is kept so the console and the
 * QA DTO read in their own vocabulary.
 */
export const QA_SYNC_OUTCOMES = TERMINAL_OUTCOMES;

export type QaSyncOutcome = (typeof QA_SYNC_OUTCOMES)[number];

/**
 * What is currently forced for one QA test domain.
 *
 * Every field is null when nothing is overridden. The console renders this so a
 * tester can see which levers are live — previously the only way to know was to
 * remember, because every QA endpoint was a write with no readback and each
 * toggle held nothing but local component state.
 *
 * These are ephemeral Redis keys with ~1h TTLs, so a state read is a snapshot:
 * a lever can expire between this call and the next action.
 */
export interface QaOverridesState {
  domain: string;
  /** Null when the domain has no tenant — the customer-keyed levers can't apply. */
  customerId: number | null;
  /** Forces `GET /authentication/dwd-client-id` to fail (Step-3 load error). */
  clientIdFail: boolean;
  /** Forces the Step-3 DWD verify result. */
  dwdStatus: DwdCheckStatus | null;
  /** Forces the Step-4 sync KICKOFF to fail ("Sync Could Not Start"). */
  syncKickoffFail: boolean;
  /**
   * Arms a terminal outcome for the NEXT sync: the kickoff proceeds normally
   * (seeding the notification list), the real enqueue is skipped, and this
   * outcome is reported immediately.
   */
  syncOutcome: QaSyncOutcome | null;
  /** Forces a login block verdict. */
  loginVerdict: ForceableBlockVerdict | null;
  /**
   * The seeded license scenario, or null.
   *
   * Distinguishable only because seeding stamps a marker onto the cached facts.
   * The QA license lever writes to the REAL license cache key rather than a
   * separate override key, so without that marker a naturally-cached Chargify
   * read would be indistinguishable from a QA seed — and this panel would claim
   * an override that nobody set.
   */
  licenseScenario: LicenseQaScenario | 'unavailable' | null;
  /**
   * Which onboarding endpoints are currently forced to fail.
   *
   * An ARRAY, not a single value: each endpoint has its own key and they are
   * independently armable, so several can be live at once. Empty when none are.
   */
  endpointFail: QaForceableEndpoint[];
  /** Artificial delay on every `/onboarding` request, in ms. Null when unset. */
  spinnerDelayMs: number | null;
  /**
   * Forces Mandrill to "reject" the Step-5 invitations, so the send-failure
   * branches are reachable. Distinct from the `invite-send` forced failure,
   * which fails the HTTP request instead — see {@link qaInviteRejectKey}.
   */
  inviteReject: QaInviteRejectMode | null;
}

/**
 * Levers that are one-shot actions rather than overrides, so they never appear
 * in {@link QaOverridesState} and are not undone by clear-all. Only clear-terms
 * qualifies: it deletes acceptance rows rather than setting a marker, and
 * "clear the overrides" should not quietly destroy data.
 *
 * Note the sync-outcome lever is NOT one of these — it arms a Redis key and is
 * cleared like any other override. But once it has FIRED, the run it produced is
 * real state (a `lastOutcome` indistinguishable from a genuine failure), and
 * clearing the lever does not rewind that. Re-run the sync, or reset onboarding.
 */
export const QA_ONE_SHOT_ACTIONS = ['clear-terms'] as const;
